/**
 * Solanaland
 *
 * Author: Phia Nasol
 * Company: Digiteer Inc.
 *
 * https://www.digiteer.digital/
 *
 */

import React from "react";
import { Col, Container, Modal, Row, Spinner } from "react-bootstrap";
import { Tab, Tabs, TabList, TabPanel } from "react-tabs";
import { Alert, Button, Card, Input, Select, SvgIcon, Table } from "core/components";
import {
  admin_material_requests_path,
  admin_material_request_path,
  approve_material_request_admin_project_path,
  delete_material_request_admin_project_path,
  print_issuance_admin_material_request_path,
  print_request_admin_material_request_path,
  reject_material_request_admin_project_path,
  release_admin_material_request_path
} from "routes";
import { navigateTo } from "core/navigation";
import { api, ApiError } from "core/api";
import { MaterialRequestStatus } from "core/schema";
import MaterialRequestHistory from "./MaterialRequestHistory";
import ProjectMaterialRequest from "./ProjectMaterialRequest";
import { useForm } from "react-hook-form";

const MaterialRequests = (): JSX.Element => {
  const {
    control,
    handleSubmit,
    reset,
    getValues,
    setValue,
    formState: { isSubmitting, isValidating }
  } = useForm({
    mode: "onChange"
  });
  const [loading, setLoading] = React.useState(false);
  const [isApproving, setIsApproving] = React.useState(false);
  const [materialRequests, setMaterialRequests] = React.useState([]);
  const [materialRequest, setMaterialRequest] = React.useState(null);
  const [showMaterialRequestModal, setShowMaterialRequestModal] = React.useState(false);
  const [showApproveMaterialRequestModal, setShowApproveMaterialRequestModal] = React.useState(false);
  const [showRejectMaterialRequestModal, setShowRejectMaterialRequestModal] = React.useState(false);
  const [showDeleteMaterialRequestModal, setShowDeleteMaterialRequestModal] = React.useState(false);
  const [showReleaseConfirmationModal, setShowReleaseConfirmationModal] = React.useState(false);
  const [statusFilter, setStatusFilter] = React.useState("");
  const isForUser = window.location.href.includes("project_material_requests");
  const [statuses, setStatuses] = React.useState([]);
  const [formErrors, setFormErrors] = React.useState<Array<string>>([]);
  const [itemsForDestruction, setItemsForDestruction] = React.useState([]);
  const [hasMaterialRequestHistory, setHasMaterialRequestHistory] = React.useState(false);
  const [hasWarehousesAccess, setHasWarehousesAccess] = React.useState(false);
  const table = React.useRef(null);

  const columns = React.useMemo(
    () => [
      {
        id: "id",
        Header: "Id",
        accessor: "id"
      },
      {
        id: 1,
        Header: "Material Request",
        accessor: "code",
        Cell: (props) => {
          return <p className="text-center">{props.value}</p>;
        }
      },
      {
        id: 4,
        Header: "Project",
        accessor: "project.code",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-truncate text-center">{props.value}</p>
            </div>
          );
        }
      },
      {
        id: 3,
        Header: "Warehouse",
        accessor: "warehouse.name",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-truncate text-center">{props.value}</p>
            </div>
          );
        }
      },
      {
        Header: "Requested By",
        accessor: "created_by",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-truncate text-center">{props.value}</p>
            </div>
          );
        }
      },
      {
        Header: "Approved By",
        accessor: "approved_by",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-truncate text-center">{props.value || "-"}</p>
            </div>
          );
        }
      },
      {
        id: "created date",
        accessor: "created_at"
      },
      {
        Header: "Released By",
        accessor: "released_by",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-truncate text-center">{props.value || "-"}</p>
            </div>
          );
        }
      },
      {
        Header: "Total Items",
        accessor: "total_items",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-truncate text-center">{props.value}</p>
            </div>
          );
        }
      },
      {
        id: "status",
        Header: "Status",
        accessor: "status",
        Cell: (props) => {
          const status = I18n.t(`activerecord.attributes.material_request.statuses.${props.value}`);

          return (
            <div
              className={`d-flex justify-content-center badge badge--pill badge--${props.value.replace("_", "-")}`}
              title={status}
              data-bs-toggle="tooltip"
            >
              <p className="text-truncate">{status}</p>
            </div>
          );
        }
      },
      {
        Header: "Actions",
        accessor: "",
        Cell: (props) => {
          const { status, policy } = props.row.original;
          const isOpen = status == MaterialRequestStatus.Open;
          const isForApproval = status == MaterialRequestStatus.ForApproval;
          const isClosed = status == MaterialRequestStatus.Closed;

          return (
            <div className="d-flex justify-content-center">
              <a
                className="button button--action button--action-success"
                title="View Material Request"
                data-bs-toggle="tooltip"
                onClick={() => {
                  if (isForUser) {
                    setMaterialRequest(props.row.original);
                    setShowMaterialRequestModal(!showMaterialRequestModal);
                  } else navigateTo(admin_material_request_path(props.row.original.id));
                }}
              >
                <SvgIcon name="view" className="h5" />
              </a>
              {isForUser && !isClosed && !isForApproval && (
                <a
                  className="button button--action button--action-primary"
                  title="Print Material Request"
                  data-bs-toggle="tooltip"
                  onClick={() => {
                    window.open(print_request_admin_material_request_path(props.row.original.id));
                  }}
                >
                  <SvgIcon name="printer" className="h5" />
                </a>
              )}
              {isClosed && (
                <a
                  className="button button--action button--action-primary"
                  title="Print Material Issuance"
                  data-bs-toggle="tooltip"
                  onClick={() => {
                    window.open(print_issuance_admin_material_request_path(props.row.original.id));
                  }}
                >
                  <SvgIcon name="printer" className="h5" />
                </a>
              )}
              {isForUser && isForApproval && (
                <>
                  {policy.approve_material_request && (
                    <a
                      className="button button--action button--action-approve"
                      title="Approve Material Request"
                      data-bs-toggle="tooltip"
                      onClick={() => {
                        setMaterialRequest(props.row.original);
                        setShowApproveMaterialRequestModal(!showApproveMaterialRequestModal);
                      }}
                    >
                      <SvgIcon name="check-circle" className="h6" />
                    </a>
                  )}
                  {policy.reject_material_request && (
                    <a
                      className="button button--action button--action-danger"
                      title="Reject Material Request"
                      data-bs-toggle="tooltip"
                      onClick={() => {
                        setMaterialRequest(props.row.original);
                        setShowRejectMaterialRequestModal(!showRejectMaterialRequestModal);
                      }}
                    >
                      <SvgIcon name="times-circle" className="h6" />
                    </a>
                  )}
                </>
              )}
              {isForUser && (isOpen || isForApproval) && (
                <a
                  className="button button--action button--action-danger"
                  title="Delete Material Request"
                  data-bs-toggle="tooltip"
                  onClick={() => {
                    setMaterialRequest(props.row.original);
                    setShowDeleteMaterialRequestModal(!showDeleteMaterialRequestModal);
                  }}
                >
                  <SvgIcon name="delete" className="h6" />
                </a>
              )}
            </div>
          );
        }
      }
    ],
    []
  );

  const getMaterialRequests = async () => {
    try {
      setLoading(true);
      const response = await api.get(admin_material_requests_path({ is_for_user: isForUser }));
      setMaterialRequests(response.data.data);
      setHasMaterialRequestHistory(response.data.has_material_request_history);
      setHasWarehousesAccess(response.data.has_warehouses_access);
      setStatuses(response.data.statuses);
    } catch (error) {
      if (error?.message.includes("Cannot read properties of null (reading 'setFilter')")) return;
      if (error instanceof ApiError) {
        setFormErrors(error.all());
      } else {
        setFormErrors([error?.message]);
      }
    } finally {
      setLoading(false);

      const queryString = location.search;
      const urlParams = new URLSearchParams(queryString);
      const status = urlParams.get("status");

      if (status && status === "for_approval") {
        setStatusFilter(status);
        table.current.setFilter("status", status);
      }
    }
  };

  const releaseMaterialRequest = async (id) => {
    try {
      setLoading(true);
      await api.put(release_admin_material_request_path(id));
      navigateTo(admin_material_requests_path());
    } catch (error) {
      if (error instanceof ApiError) {
        setFormErrors(error.all());
      } else {
        setFormErrors([error?.message]);
      }
    } finally {
      setLoading(false);
    }
  };

  const updateMaterialRequests = (materialRequests, updatedMaterialRequest) => {
    const updatedMaterialRequests = materialRequests.map((materialRequest) => {
      if (materialRequest.id == updatedMaterialRequest.id) {
        return { ...materialRequest, ...updatedMaterialRequest };
      }

      return materialRequest;
    });

    setMaterialRequests(updatedMaterialRequests);
  };

  const approveMaterialRequest = React.useCallback(async (materialRequests, materialRequestId, projectId) => {
    try {
      setIsApproving(true);
      const response = await api.put(approve_material_request_admin_project_path({ id: projectId }, { material_request_id: materialRequestId }));
      updateMaterialRequests(materialRequests, response.data);
    } catch (error) {
      if (error instanceof ApiError) {
        setFormErrors(error.all());
      } else {
        setFormErrors([error?.message]);
      }
    } finally {
      setIsApproving(false);
    }
  }, []);

  const rejectMaterialRequest = React.useCallback(async (materialRequests, materialRequestId, projectId) => {
    try {
      const response = await api.put(reject_material_request_admin_project_path({ id: projectId }, { material_request_id: materialRequestId }));
      updateMaterialRequests(materialRequests, response.data);
    } catch (error) {
      if (error instanceof ApiError) {
        setFormErrors(error.all());
      } else {
        setFormErrors([error?.message]);
      }
    }
  }, []);

  const deleteMaterialRequest = React.useCallback(async (materialRequests, materialRequestId, projectId) => {
    try {
      const response = await api.deleteItem(
        delete_material_request_admin_project_path({ id: projectId }, { material_request_id: materialRequestId })
      );
      setMaterialRequests(materialRequests.filter((materialRequest) => materialRequest.id != response.data.id));
    } catch (error) {
      if (error instanceof ApiError) {
        setFormErrors(error.all());
      } else {
        setFormErrors([error?.message]);
      }
    }
  }, []);

  const withItemsForDestruction = (data) => {
    const { material_request_items } = data;

    return { ...data, material_request_items: [...material_request_items, ...itemsForDestruction] };
  };

  const save = async (data): Promise<unknown> => {
    try {
      await api.put(admin_material_request_path(data.id), {
        ...withItemsForDestruction(data)
      });
      await getMaterialRequests();
    } catch (error) {
      if (error instanceof ApiError) {
        setFormErrors(error.all());
      } else {
        setFormErrors([error?.message]);
      }
    } finally {
      setShowMaterialRequestModal(!showMaterialRequestModal);
    }
  };

  const onError = (data) => {
    console.error(data);
  };

  React.useEffect(() => {
    getMaterialRequests();
  }, []);

  const loader = () => {
    return (
      <div className="d-flex justify-content-center align-items-center h-100 pt-lg-8 pb-lg-8">
        <Spinner animation="border" role="status" size="lg" variant="brand">
          <span className="visually-hidden">Loading Material Requests...</span>
        </Spinner>
      </div>
    );
  };

  const noContent = () => {
    return (
      <Container fluid={true} className="text-center pt-lg-8 pb-lg-8">
        <Row>
          <Col>
            <SvgIcon name="clipboard-list" className="display-icon text-gray" />
          </Col>
        </Row>
        <Row>
          <Col>
            <h5>No material requests.</h5>
          </Col>
        </Row>
      </Container>
    );
  };

  const dataFilter = () => {
    return (
      <Row className="mb-3">
        <Col>
          <Input placeholder="Search for Material Request Number" onChange={(e) => table.current.setFilter(1, e.target.value)} />
        </Col>
        <Col>
          <Input placeholder="Search for Project" onChange={(e) => table.current.setFilter(4, e.target.value)} />
        </Col>
        {hasWarehousesAccess && (
          <Col>
            <Input placeholder="Search for Warehouse" onChange={(e) => table.current.setFilter(3, e.target.value)} />
          </Col>
        )}
        <Col>
          <Select
            placeholder="Filter by Status"
            options={statuses}
            isSearchable={false}
            getOptionLabel={(option) => I18n.t(`activerecord.attributes.material_request.statuses.${option.value}`)}
            getOptionValue={(option) => option.value}
            onChange={(newOption) => {
              if (newOption) {
                setStatusFilter(newOption.value);
                table.current.setFilter("status", newOption.value);
              } else {
                setStatusFilter("");
                table.current.setFilter("status", "");
              }
            }}
            value={statuses.find((status) => status.value == statusFilter) || ""}
          />
        </Col>
      </Row>
    );
  };

  const dataTable = () => {
    return (
      <Row>
        <Col>
          <Table columns={columns} data={materialRequests} ref={table} />
        </Col>
      </Row>
    );
  };

  const content = () => {
    return materialRequests.length > 0 ? (
      <>
        {dataFilter()}
        {dataTable()}
      </>
    ) : (
      noContent()
    );
  };

  return (
    <React.StrictMode>
      {formErrors && formErrors.length != 0 && (
        <Alert>
          {formErrors.map((error, index) => {
            return <div key={index}>{error}</div>;
          })}
        </Alert>
      )}
      <Tabs className="card" selectedTabClassName="project-tab__link project-tab__link--active">
        <Container fluid={true}>
          <Row className="d-flex align-items-baseline">
            <Col>
              <TabList className="card__header" style={{ padding: 0 }}>
                <Col className="d-flex justify-content-start">
                  <Tab className="project-tab__link">Today</Tab>
                  <Tab className="project-tab__link">History</Tab>
                </Col>
              </TabList>
            </Col>
          </Row>
          <TabPanel>
            <Card.Body>{loading ? loader() : content()}</Card.Body>
          </TabPanel>
          <TabPanel>
            <MaterialRequestHistory hasMaterialRequestHistory={hasMaterialRequestHistory} hasWarehousesAccess={hasWarehousesAccess} />
          </TabPanel>
        </Container>
      </Tabs>
      <Modal dialogClassName="modal-dialog-centered" show={showReleaseConfirmationModal}>
        <Modal.Header>{`Release Material Request: ${materialRequest?.code}`}</Modal.Header>
        <Modal.Body>{`Are you sure you want to release Material Request to Project: ${materialRequest?.project?.code}?`}</Modal.Body>
        <Modal.Footer>
          <Button
            onClick={() => {
              setShowReleaseConfirmationModal(!showReleaseConfirmationModal);
            }}
            icon="times"
            title="Cancel"
          />
          <Button
            className="button--success"
            icon="save"
            title="Yes, release material request."
            onClick={() => {
              setShowReleaseConfirmationModal(!showReleaseConfirmationModal);
              releaseMaterialRequest(materialRequest?.id).catch(console.log(error));
            }}
          />
        </Modal.Footer>
      </Modal>
      <Modal dialogClassName="modal-dialog-centered modal-xl" show={showMaterialRequestModal}>
        <Modal.Header style={{ display: "block" }}>
          {isForUser ? (
            <>
              <p>{`Material Request: ${materialRequest?.code}`}</p>
              <p>{`Project ID: ${materialRequest?.project?.code}`}</p>
              <p>{`Requestor: ${materialRequest?.created_by}`}</p>
            </>
          ) : (
            `Material Request: ${materialRequest?.code}`
          )}
        </Modal.Header>
        <Modal.Body>
          <ProjectMaterialRequest
            materialRequest={materialRequest}
            control={control}
            reset={reset}
            getValues={getValues}
            setValue={setValue}
            setItemsForDestruction={setItemsForDestruction}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button
            onClick={() => {
              setShowMaterialRequestModal(!showMaterialRequestModal);
            }}
            icon="times"
            title="Close"
          />
          {[MaterialRequestStatus.Open, MaterialRequestStatus.ForApproval].includes(materialRequest?.status) && (
            <Button
              className="button--brand"
              icon="save"
              title="Save"
              disabled={isSubmitting || isValidating}
              onClick={() => {
                handleSubmit(async (data) => await save(data), onError)();
              }}
            />
          )}
        </Modal.Footer>
      </Modal>
      <Modal dialogClassName="modal-dialog-centered" show={showApproveMaterialRequestModal}>
        <Modal.Header>{`Approve Material Request: ${materialRequest?.code}`}</Modal.Header>
        <Modal.Body>{`Are you sure you want to approve material request: ${materialRequest?.code}?`}</Modal.Body>
        <Modal.Footer>
          <Button
            onClick={() => {
              setShowApproveMaterialRequestModal(!showApproveMaterialRequestModal);
            }}
            icon="times"
            title="Cancel"
          />
          <Button
            className="button--brand"
            icon="save"
            title="Yes, approve material request."
            disabled={isApproving}
            onClick={() => {
              approveMaterialRequest(materialRequests, materialRequest?.id, materialRequest?.project?.id).then(() => {
                setShowApproveMaterialRequestModal(!showApproveMaterialRequestModal);
              });
            }}
          />
        </Modal.Footer>
      </Modal>
      <Modal dialogClassName="modal-dialog-centered" show={showRejectMaterialRequestModal}>
        <Modal.Header>{`Reject Material Request: ${materialRequest?.code}`}</Modal.Header>
        <Modal.Body>{`Are you sure you want to reject material request: ${materialRequest?.code}?`}</Modal.Body>
        <Modal.Footer>
          <Button
            onClick={() => {
              setShowRejectMaterialRequestModal(!showRejectMaterialRequestModal);
            }}
            icon="times"
            title="Cancel"
          />
          <Button
            className="button--danger"
            icon="times-circle"
            title="Yes, reject material request."
            onClick={() => {
              rejectMaterialRequest(materialRequests, materialRequest?.id, materialRequest?.project?.id).then(() => {
                setShowRejectMaterialRequestModal(!showRejectMaterialRequestModal);
              });
            }}
          />
        </Modal.Footer>
      </Modal>
      <Modal dialogClassName="modal-dialog-centered" show={showDeleteMaterialRequestModal}>
        <Modal.Header>{`Delete Material Request: ${materialRequest?.code}`}</Modal.Header>
        <Modal.Body>{`Are you sure you want to delete material request: ${materialRequest?.code}?`}</Modal.Body>
        <Modal.Footer>
          <Button
            onClick={() => {
              setShowDeleteMaterialRequestModal(!showDeleteMaterialRequestModal);
            }}
            icon="times"
            title="Cancel"
          />
          <Button
            className="button--danger"
            icon="delete"
            title="Yes, delete material request."
            onClick={() => {
              deleteMaterialRequest(materialRequests, materialRequest?.id, materialRequest?.project?.id).then(() => {
                setShowDeleteMaterialRequestModal(!showDeleteMaterialRequestModal);
              });
            }}
          />
        </Modal.Footer>
      </Modal>
    </React.StrictMode>
  );
};

export default MaterialRequests;
